import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [consultationSubmitStatus, setConsultationSubmitStatus] = useState<'success' | 'error' | null>(null);

  // Handle scroll effect for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setScrolled(window.scrollY > 50);
    });
  }

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleConsultationSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate required fields
    const requiredFields = ['name', 'email', 'phone', 'contact_method', 'vehicle_info'];
    for (const field of requiredFields) {
      if (!formData.get(field)) {
        alert('Please fill in all required fields');
        return;
      }
    }

    // Validate message length
    const additionalInfo = formData.get('additional_info') as string;
    if (additionalInfo && additionalInfo.length > 500) {
      alert('Additional information must be 500 characters or less');
      return;
    }

    // Collect checkbox values for concerns
    const concerns: string[] = [];
    const concernsCheckboxes = form.querySelectorAll('input[name="concerns"]:checked');
    concernsCheckboxes.forEach((checkbox) => {
      concerns.push((checkbox as HTMLInputElement).value);
    });

    // Create URL encoded data
    const submitData = new URLSearchParams();
    submitData.append('name', formData.get('name') as string);
    submitData.append('email', formData.get('email') as string);
    submitData.append('phone', formData.get('phone') as string);
    submitData.append('contact_method', formData.get('contact_method') as string);
    submitData.append('vehicle_info', formData.get('vehicle_info') as string);
    if (concerns.length > 0) {
      submitData.append('concerns', concerns.join(', '));
    }
    const preferredDatetime = formData.get('preferred_datetime') as string;
    if (preferredDatetime) {
      submitData.append('preferred_datetime', preferredDatetime);
    }
    if (additionalInfo) {
      submitData.append('additional_info', additionalInfo);
    }

    // Submit form
    fetch('https://readdy.ai/api/form/d5detfcf78kfudd4kkb0', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: submitData.toString()
    })
    .then(response => {
      if (response.ok) {
        setConsultationSubmitStatus('success');
        form.reset();
        setTimeout(() => setConsultationSubmitStatus(null), 5000);
      } else {
        setConsultationSubmitStatus('error');
        setTimeout(() => setConsultationSubmitStatus(null), 5000);
      }
    })
    .catch(() => {
      setConsultationSubmitStatus('error');
      setTimeout(() => setConsultationSubmitStatus(null), 5000);
    });
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/4855067b8186d18c626a2155d1a193ae/154d0532d8fc27f45dd426f8e055f5b4.png" 
                alt="Mobile Car Detailing Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className={`text-xl font-bold tracking-tight ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                Reflecto Auto Detailing
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className={`text-sm font-medium tracking-wide uppercase transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                Home
              </button>
              <button onClick={() => scrollToSection('services')} className={`text-sm font-medium tracking-wide uppercase transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                Services
              </button>
              <button onClick={() => scrollToSection('about')} className={`text-sm font-medium tracking-wide uppercase transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                About Us
              </button>
              <button onClick={() => scrollToSection('gallery')} className={`text-sm font-medium tracking-wide uppercase transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                Gallery
              </button>
              <button onClick={() => scrollToSection('pricing')} className={`text-sm font-medium tracking-wide uppercase transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                Pricing
              </button>
              <button onClick={() => scrollToSection('contact')} className={`text-sm font-medium tracking-wide uppercase transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                Contact
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
            >
              <i className={`ri-menu-line text-2xl ${scrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 cursor-pointer">
                Home
              </button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 cursor-pointer">
                Services
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 cursor-pointer">
                About Us
              </button>
              <button onClick={() => scrollToSection('gallery')} className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 cursor-pointer">
                Gallery
              </button>
              <button onClick={() => scrollToSection('pricing')} className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 cursor-pointer">
                Pricing
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 cursor-pointer">
                Contact
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1619405399517-d7fce0f13302?w=1920&h=1080&fit=crop&q=80" 
            alt="Professional Car Detailing" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-teal-500/90 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-map-pin-line"></i>
              <span className="whitespace-nowrap">Mobile Service • We Come To You</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Premium Mobile<br />
              Car Detailing<br />
              At Your Convenience
            </h1>
            
            <p className="text-xl text-white/90 mb-8 max-w-2xl">
              Professional automotive detailing services delivered to your location. From basic cleaning to advanced paint correction and ceramic coatings.
            </p>

            <div className="mb-4">
              <span className="text-white text-lg font-semibold tracking-wide uppercase">Book Now</span>
            </div>

            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => {
                  const pricingSection = document.getElementById('pricing');
                  if (pricingSection) {
                    pricingSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-teal-500 text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-teal-600 transition-all hover:shadow-xl hover:scale-105 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <span>DETAIL</span>
                <i className="ri-car-line"></i>
              </button>
              <button 
                onClick={() => {
                  const consultationSection = document.getElementById('consultation');
                  if (consultationSection) {
                    consultationSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-white hover:text-gray-900 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <span>CONSULTATION</span>
                <i className="ri-chat-3-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 text-teal-500 text-sm font-bold tracking-widest uppercase mb-4">
              <i className="ri-star-line"></i>
              <span>OUR EXPERTISE</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Premium Detailing Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive car care solutions from basic cleaning to advanced protection
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Exterior Detailing */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <img 
                  src="https://public.readdy.ai/ai/img_res/15f7f5c6fbc407c9f76e17dfb5adc65e.jpg" 
                  alt="Exterior Detailing" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-car-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Exterior Detailing</h3>
                <ul className="space-y-2 text-white/90 mb-4 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-500 mt-0.5"></i>
                    <span>Hand wash and foam bath</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-500 mt-0.5"></i>
                    <span>Decontamination treatment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-500 mt-0.5"></i>
                    <span>Polish and wax application</span>
                  </li>
                </ul>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Interior Detailing */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20car%20interior%20detailing%20service%20showing%20expert%20cleaning%20vehicle%20cabin%20with%20vacuum%20and%20specialized%20tools%2C%20meticulous%20automotive%20interior%20care%20with%20attention%20to%20seats%20and%20surfaces%2C%20high-quality%20interior%20detailing%20process%20with%20professional%20equipment%2C%20skilled%20detailer%20carefully%20cleaning%20car%20interior%20upholstery%20and%20dashboard%2C%20comprehensive%20interior%20cleaning%20demonstration&width=600&height=800&seq=interior-detailing-001&orientation=portrait" 
                  alt="Interior Detailing" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-steering-2-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Interior Detailing</h3>
                <ul className="space-y-2 text-white/90 mb-4 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-500 mt-0.5"></i>
                    <span>Deep vacuum and extraction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-500 mt-0.5"></i>
                    <span>Leather conditioning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-500 mt-0.5"></i>
                    <span>Carpet and upholstery shampoo</span>
                  </li>
                </ul>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Paint Correction */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Close-up%20macro%20photography%20of%20glossy%20car%20paint%20surface%20showing%20perfect%20mirror-like%20reflection%20after%20professional%20paint%20correction%2C%20flawless%20automotive%20finish%20with%20deep%20shine%20and%20clarity%2C%20pristine%20vehicle%20paintwork%20displaying%20exceptional%20depth%20and%20gloss%2C%20high-end%20detailing%20results%20with%20smooth%20polished%20surface%2C%20luxury%20car%20paint%20detail%20with%20perfect%20reflections%2C%20professional%20automotive%20paint%20care%20showcase&width=600&height=800&seq=paint-correction-001&orientation=portrait" 
                  alt="Paint Correction" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute top-4 right-4">
                <span className="bg-teal-500/90 text-white px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap">
                  ADVANCED
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="text-2xl font-bold text-white mb-3">Paint Correction</h3>
                <p className="text-white/90 mb-4">
                  Multi-stage polishing to remove swirls, scratches, and imperfections for a flawless finish
                </p>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Ceramic Coating */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Stunning%20close-up%20of%20water%20droplets%20beading%20perfectly%20on%20ceramic%20coated%20car%20surface%2C%20hydrophobic%20effect%20demonstration%20on%20glossy%20vehicle%20paint%2C%20professional%20ceramic%20coating%20application%20results%20showing%20extreme%20water%20repellency%2C%20pristine%20automotive%20finish%20with%20protective%20coating%2C%20high-tech%20car%20paint%20protection%20with%20water%20beading%20effect%2C%20luxury%20vehicle%20surface%20treatment%20showcase&width=800&height=600&seq=ceramic-coating-001&orientation=landscape" 
                  alt="Ceramic Coating" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-shield-star-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Ceramic Coating</h3>
                <p className="text-gray-300 mb-4">
                  Long-lasting protection with hydrophobic properties and enhanced gloss
                </p>
                <p className="text-teal-500 font-bold text-lg mb-4">Starting at $99</p>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Pet Hair Removal */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20detailer%20removing%20pet%20hair%20from%20car%20interior%20using%20specialized%20tools%20and%20techniques%2C%20expert%20cleaning%20pet%20fur%20from%20vehicle%20seats%20and%20carpets%2C%20automotive%20interior%20pet%20hair%20removal%20service%20demonstration%2C%20meticulous%20car%20cleaning%20process%20for%20pet%20owners%2C%20professional%20pet%20hair%20extraction%20from%20vehicle%20upholstery%20with%20specialized%20equipment&width=600&height=800&seq=pet-hair-removal-001&orientation=portrait" 
                  alt="Pet Hair Removal" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-scissors-cut-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Pet Hair Removal</h3>
                <p className="text-white/90 mb-4">
                  Specialized tools and techniques to eliminate stubborn pet hair from all surfaces
                </p>
                <p className="text-teal-500 font-bold text-lg mb-4">Starting at $29</p>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Headlight Restoration */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Before%20and%20after%20headlight%20restoration%20comparison%20showing%20dramatic%20clarity%20improvement%2C%20split%20view%20of%20yellowed%20foggy%20headlight%20versus%20crystal%20clear%20restored%20headlight%2C%20professional%20headlight%20restoration%20results%2C%20clear%20contrast%20between%20oxidized%20and%20professionally%20restored%20automotive%20headlights%2C%20impressive%20headlight%20clarity%20transformation&width=600&height=800&seq=headlight-restoration-002&orientation=portrait" 
                  alt="Headlight Restoration" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Headlight Restoration</h3>
                <p className="text-white/90 mb-4">
                  Restore clarity and brightness to oxidized and yellowed headlights
                </p>
                <p className="text-teal-500 font-bold text-lg mb-4">Starting at $49</p>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Engine Bay Detail */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Pristine%20clean%20car%20engine%20bay%20after%20professional%20detailing%20service%2C%20immaculate%20automotive%20engine%20compartment%20with%20spotless%20components%20and%20polished%20surfaces%2C%20detailed%20vehicle%20engine%20showing%20meticulous%20cleaning%20results%2C%20high-end%20engine%20bay%20detailing%20with%20clean%20hoses%20and%20shiny%20metal%20parts%2C%20professional%20automotive%20engine%20cleaning%20showcase%20with%20organized%20and%20pristine%20appearance%2C%20luxury%20car%20engine%20detail%20with%20perfect%20finish&width=1000&height=600&seq=engine-bay-detail-001&orientation=landscape" 
                  alt="Engine Bay Detail" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-settings-3-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Engine Bay Detail</h3>
                <p className="text-gray-300 mb-4">
                  Professional cleaning and dressing of your engine compartment for a showroom-ready appearance
                </p>
                <p className="text-teal-500 font-bold text-lg mb-4">Starting at $49</p>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Scratch Removal */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Before%20and%20after%20comparison%20of%20car%20paint%20scratch%20removal%20showing%20dramatic%20improvement%2C%20split%20view%20of%20scratched%20vehicle%20surface%20versus%20perfectly%20smooth%20repaired%20paint%2C%20professional%20automotive%20scratch%20repair%20results%2C%20clear%20contrast%20between%20damaged%20and%20restored%20car%20paint%2C%20impressive%20paint%20restoration%20with%20flawless%20finish%2C%20expert%20scratch%20removal%20transformation%20showcase&width=800&height=600&seq=scratch-removal-001&orientation=landscape" 
                  alt="Scratch Removal" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-brush-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Scratch Removal</h3>
                <p className="text-gray-300 mb-4">
                  Expert paint correction to eliminate minor to moderate scratches and restore your vehicle's finish
                </p>
                <p className="text-teal-500 font-bold text-lg mb-4">Starting at $29</p>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Clay Bar Treatment */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20car%20detailer%20performing%20clay%20bar%20treatment%20on%20vehicle%20paint%20surface%2C%20expert%20hand%20carefully%20working%20clay%20bar%20across%20glossy%20car%20paint%20to%20remove%20contaminants%2C%20automotive%20clay%20bar%20decontamination%20process%20demonstration%2C%20meticulous%20paint%20preparation%20with%20clay%20bar%20technique%2C%20high-quality%20car%20care%20clay%20bar%20application%20on%20pristine%20vehicle%20surface%2C%20professional%20detailing%20clay%20bar%20treatment%20showcase&width=800&height=600&seq=clay-bar-treatment-001&orientation=landscape" 
                  alt="Clay Bar Treatment" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-drop-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Clay Bar Treatment</h3>
                <p className="text-gray-300 mb-4">
                  Deep decontamination to remove embedded particles and leave your paint silky smooth
                </p>
                <p className="text-teal-500 font-bold text-lg mb-4">Starting at $99</p>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Stain Removal Before/After */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <div className="grid grid-cols-2 h-full">
                  <div className="relative">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Real%20car%20seat%20with%20coffee%20stain%20and%20dirt%20marks%20from%20daily%20use%2C%20authentic%20vehicle%20upholstery%20showing%20realistic%20stains%20from%20spills%20and%20everyday%20wear%2C%20genuine%20automobile%20interior%20fabric%20with%20visible%20food%20and%20drink%20stains%2C%20actual%20car%20seat%20with%20natural%20staining%20from%20regular%20use%2C%20everyday%20vehicle%20upholstery%20needing%20cleaning%20with%20realistic%20marks%20and%20discoloration&width=600&height=800&seq=stain-before-realistic-001&orientation=portrait" 
                      alt="Before Stain Removal" 
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-4 left-4 bg-white/90 px-3 py-1 rounded-full text-xs font-bold text-gray-900">
                      BEFORE
                    </div>
                  </div>
                  <div className="relative">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Pristine%20clean%20car%20seat%20upholstery%20with%20identical%20fabric%20pattern%20and%20texture%20showing%20zero%20stains%20or%20marks%2C%20perfectly%20spotless%20vehicle%20interior%20fabric%20matching%20the%20exact%20same%20material%20weave%20and%20color%20as%20stained%20version%20but%20completely%20clean%2C%20immaculate%20automobile%20seat%20with%20same%20cloth%20pattern%20now%20flawlessly%20clean%20without%20any%20discoloration%2C%20exact%20same%20car%20seat%20fabric%20type%20completely%20stain-free%20and%20fresh%20looking%2C%20identical%20upholstery%20material%20perfectly%20cleaned%20with%20all%20marks%20eliminated&width=600&height=800&seq=stain-after-pristine-clean-003&orientation=portrait" 
                      alt="After Stain Removal" 
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-4 right-4 bg-teal-500/90 px-3 py-1 rounded-full text-xs font-bold text-white">
                      AFTER
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-drop-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Stain Removal</h3>
                <p className="text-gray-300 mb-4">
                  Professional extraction and treatment to eliminate tough stains from upholstery and carpets
                </p>
                <p className="text-teal-500 font-bold text-lg mb-4">Starting at $29</p>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Plastic Restoration */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Before%20and%20after%20car%20plastic%20trim%20restoration%20showing%20dramatic%20improvement%2C%20split%20view%20of%20faded%20gray%20plastic%20trim%20versus%20restored%20deep%20black%20finish%2C%20professional%20automotive%20plastic%20restoration%20results%2C%20clear%20contrast%20between%20oxidized%20and%20professionally%20restored%20vehicle%20trim%2C%20impressive%20plastic%20rejuvenation%20with%20rich%20black%20color%2C%20expert%20trim%20restoration%20transformation%20showcase%20on%20car%20exterior&width=800&height=600&seq=plastic-restoration-001&orientation=landscape" 
                  alt="Plastic Restoration" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-refresh-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Plastic Restoration</h3>
                <p className="text-gray-300 mb-4">
                  Restore faded exterior trim and plastic components to their original deep black finish
                </p>
                <p className="text-teal-500 font-bold text-lg mb-4">Starting at $39</p>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Bundle Promotion */}
          <div className="mt-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTEwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHptMC0xMGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-white/20 text-white px-6 py-2 rounded-full text-sm font-bold mb-6">
                <i className="ri-gift-line text-xl"></i>
                <span>LIMITED TIME OFFER</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Bundle Services & Save Up To 20%
              </h3>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Combine multiple services in one appointment and enjoy significant savings. The more services you bundle, the more you save!
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button 
                  onClick={() => scrollToSection('pricing')}
                  className="bg-white text-teal-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  VIEW PACKAGES
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                >
                  GET CUSTOM QUOTE
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://static.readdy.ai/image/4855067b8186d18c626a2155d1a193ae/154d0532d8fc27f45dd426f8e055f5b4.png" 
                  alt="Our Team" 
                  className="rounded-2xl shadow-2xl w-full h-96 object-cover object-top"
                />
              </div>
            </div>

            <div>
              <div className="text-teal-500 text-sm font-bold tracking-widest uppercase mb-4">
                ABOUT US
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Passion-Driven Mobile Detailing Excellence
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                We're a team of passionate automotive enthusiasts who believe every vehicle deserves professional care. Our mobile detailing service brings premium results directly to your location, combining convenience with exceptional craftsmanship.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Using only the finest products and cutting-edge techniques, we treat every car—from daily drivers to luxury vehicles—with meticulous attention to detail. Our commitment is simple: deliver showroom-quality results while saving you time and hassle.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="text-4xl font-bold text-teal-500 mb-2">100%</div>
                  <div className="text-gray-700 font-medium">Satisfaction Guaranteed</div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="flex items-center gap-1 mb-2">
                    <i className="ri-star-fill text-2xl text-yellow-400"></i>
                    <i className="ri-star-fill text-2xl text-yellow-400"></i>
                    <i className="ri-star-fill text-2xl text-yellow-400"></i>
                    <i className="ri-star-fill text-2xl text-yellow-400"></i>
                    <i className="ri-star-fill text-2xl text-yellow-400"></i>
                  </div>
                  <div className="text-gray-700 font-medium">5-Star Rated Service</div>
                </div>
              </div>

              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-gray-900 text-white px-8 py-4 rounded-full font-semibold hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
              >
                GET STARTED
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-teal-500 text-sm font-bold tracking-widest uppercase mb-4">
              TRANSFORMATIONS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              See The Difference
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real results from our professional detailing services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Exterior Detailing Before/After */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer group">
              <div className="grid grid-cols-2 h-96">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Real%20dirty%20car%20exterior%20covered%20in%20thick%20road%20grime%20mud%20and%20dust%20from%20daily%20driving%2C%20authentic%20unwashed%20vehicle%20with%20water%20spots%20bird%20droppings%20and%20dirt%20buildup%20on%20paint%20and%20windows%2C%20genuine%20neglected%20automobile%20showing%20realistic%20dirt%20accumulation%20from%20weeks%20without%20washing%2C%20actual%20dusty%20car%20with%20visible%20contamination%20on%20body%20panels%20and%20wheels%2C%20everyday%20vehicle%20in%20need%20of%20washing%20with%20natural%20dirt%20and%20grime&width=600&height=800&seq=exterior-before-realistic-001&orientation=portrait" 
                    alt="Before Exterior Detailing" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 px-3 py-1 rounded-full text-xs font-bold text-gray-900">
                    BEFORE
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Real%20freshly%20detailed%20car%20exterior%20with%20clean%20glossy%20paint%20and%20spotless%20finish%2C%20authentic%20professionally%20washed%20vehicle%20showing%20natural%20shine%20and%20clean%20body%20panels%2C%20genuine%20detailed%20automobile%20with%20realistic%20clean%20appearance%20and%20proper%20reflections%2C%20actual%20car%20after%20professional%20wash%20with%20clean%20windows%20and%20polished%20paint%2C%20everyday%20vehicle%20after%20detailing%20with%20realistic%20clean%20look%20and%20shine&width=600&height=800&seq=exterior-after-realistic-001&orientation=portrait" 
                    alt="After Exterior Detailing" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 right-4 bg-teal-500/90 px-3 py-1 rounded-full text-xs font-bold text-white">
                    AFTER
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <span className="text-white font-semibold text-lg">Exterior Detailing Transformation</span>
              </div>
            </div>

            {/* Interior Detailing Before/After */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer group">
              <div className="grid grid-cols-2 h-96">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Real%20messy%20car%20interior%20with%20crumbs%20dirt%20and%20everyday%20clutter%20on%20seats%20and%20floor%2C%20authentic%20dirty%20vehicle%20cabin%20showing%20realistic%20dust%20on%20dashboard%20and%20stained%20fabric%20seats%2C%20genuine%20neglected%20car%20interior%20with%20visible%20dirt%20accumulation%20from%20daily%20use%2C%20actual%20cluttered%20automobile%20cabin%20with%20trash%20and%20debris%20on%20floor%20mats%2C%20everyday%20vehicle%20interior%20needing%20cleaning%20with%20natural%20wear%20and%20dirt&width=600&height=800&seq=interior-before-realistic-001&orientation=portrait" 
                    alt="Before Interior Detailing" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 px-3 py-1 rounded-full text-xs font-bold text-gray-900">
                    BEFORE
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Real%20clean%20car%20interior%20after%20professional%20detailing%20with%20vacuumed%20seats%20and%20wiped%20dashboard%2C%20authentic%20detailed%20vehicle%20cabin%20showing%20natural%20clean%20appearance%20and%20organized%20space%2C%20genuine%20cleaned%20automobile%20interior%20with%20fresh%20look%20and%20tidy%20surfaces%2C%20actual%20car%20cabin%20after%20interior%20detailing%20with%20clean%20upholstery%20and%20dust-free%20dashboard%2C%20everyday%20vehicle%20interior%20after%20professional%20cleaning%20with%20realistic%20clean%20finish&width=600&height=800&seq=interior-after-realistic-001&orientation=portrait" 
                    alt="After Interior Detailing" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 right-4 bg-teal-500/90 px-3 py-1 rounded-full text-xs font-bold text-white">
                    AFTER
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <span className="text-white font-semibold text-lg">Interior Detailing Transformation</span>
              </div>
            </div>

            {/* Paint Correction Before/After */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer group">
              <div className="grid grid-cols-2 h-96">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Real%20car%20paint%20with%20visible%20swirl%20marks%20and%20light%20scratches%20from%20automatic%20car%20washes%2C%20authentic%20vehicle%20surface%20showing%20realistic%20paint%20imperfections%20and%20minor%20clear%20coat%20damage%2C%20genuine%20automobile%20paint%20with%20natural%20wear%20showing%20spider%20webbing%20under%20direct%20light%2C%20actual%20car%20paint%20needing%20correction%20with%20visible%20fine%20scratches%20and%20dullness%2C%20everyday%20vehicle%20paint%20with%20realistic%20defects%20from%20normal%20use%20and%20washing&width=600&height=800&seq=paint-before-realistic-001&orientation=portrait" 
                    alt="Before Paint Correction" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 px-3 py-1 rounded-full text-xs font-bold text-gray-900">
                    BEFORE
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Real%20car%20paint%20after%20professional%20correction%20showing%20improved%20clarity%20and%20reduced%20swirl%20marks%2C%20authentic%20vehicle%20surface%20after%20polishing%20with%20natural%20shine%20and%20cleaner%20appearance%2C%20genuine%20automobile%20paint%20after%20correction%20with%20visible%20improvement%20in%20gloss%20and%20depth%2C%20actual%20car%20paint%20after%20professional%20treatment%20showing%20realistic%20enhancement%20and%20better%20reflections%2C%20everyday%20vehicle%20paint%20after%20correction%20with%20natural%20improved%20finish&width=600&height=800&seq=paint-after-realistic-001&orientation=portrait" 
                    alt="After Paint Correction" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 right-4 bg-teal-500/90 px-3 py-1 rounded-full text-xs font-bold text-white">
                    AFTER
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <span className="text-white font-semibold text-lg">Paint Correction Transformation</span>
              </div>
            </div>

            {/* Headlight Restoration Before/After */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer group">
              <div className="grid grid-cols-2 h-96">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Real%20yellowed%20foggy%20car%20headlight%20from%20sun%20exposure%20and%20age%2C%20authentic%20cloudy%20vehicle%20headlight%20lens%20showing%20realistic%20oxidation%20and%20reduced%20transparency%2C%20genuine%20hazy%20automobile%20headlight%20with%20natural%20yellowing%20from%20UV%20damage%2C%20actual%20aged%20car%20headlight%20with%20visible%20cloudiness%20affecting%20light%20output%2C%20everyday%20vehicle%20headlight%20needing%20restoration%20with%20realistic%20degradation&width=600&height=800&seq=headlight-before-realistic-001&orientation=portrait" 
                    alt="Before Headlight Restoration" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 px-3 py-1 rounded-full text-xs font-bold text-gray-900">
                    BEFORE
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Real%20restored%20car%20headlight%20with%20improved%20clarity%20and%20reduced%20yellowing%2C%20authentic%20vehicle%20headlight%20after%20restoration%20showing%20natural%20clear%20appearance%2C%20genuine%20automobile%20headlight%20after%20professional%20treatment%20with%20visible%20improvement%20in%20transparency%2C%20actual%20car%20headlight%20after%20restoration%20with%20realistic%20enhanced%20clarity%20and%20better%20light%20output%2C%20everyday%20vehicle%20headlight%20after%20restoration%20with%20natural%20improved%20condition&width=600&height=800&seq=headlight-after-realistic-001&orientation=portrait" 
                    alt="After Headlight Restoration" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 right-4 bg-teal-500/90 px-3 py-1 rounded-full text-xs font-bold text-white">
                    AFTER
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <span className="text-white font-semibold text-lg">Headlight Restoration Transformation</span>
              </div>
            </div>

            {/* Stain Removal Before/After */}
            <div className="relative rounded-3xl overflow-hidden hover:shadow-xl transition-all hover:scale-105 cursor-pointer flex flex-col">
              <div className="h-64">
                <div className="grid grid-cols-2 h-full">
                  <div className="relative">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Real%20car%20seat%20with%20coffee%20stain%20and%20dirt%20marks%20from%20daily%20use%2C%20authentic%20vehicle%20upholstery%20showing%20realistic%20stains%20from%20spills%20and%20everyday%20wear%2C%20genuine%20automobile%20interior%20fabric%20with%20visible%20food%20and%20drink%20stains%2C%20actual%20car%20seat%20with%20natural%20staining%20from%20regular%20use%2C%20everyday%20vehicle%20upholstery%20needing%20cleaning%20with%20realistic%20marks%20and%20discoloration&width=600&height=800&seq=stain-before-realistic-001&orientation=portrait" 
                      alt="Before Stain Removal" 
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-4 left-4 bg-white/90 px-3 py-1 rounded-full text-xs font-bold text-gray-900">
                      BEFORE
                    </div>
                  </div>
                  <div className="relative">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Pristine%20clean%20car%20seat%20upholstery%20with%20identical%20fabric%20pattern%20and%20texture%20showing%20zero%20stains%20or%20marks%2C%20perfectly%20spotless%20vehicle%20interior%20fabric%20matching%20the%20exact%20same%20material%20weave%20and%20color%20as%20stained%20version%20but%20completely%20clean%2C%20immaculate%20automobile%20seat%20with%20same%20cloth%20pattern%20now%20flawlessly%20clean%20without%20any%20discoloration%2C%20exact%20same%20car%20seat%20fabric%20type%20completely%20stain-free%20and%20fresh%20looking%2C%20identical%20upholstery%20material%20perfectly%20cleaned%20with%20all%20marks%20eliminated&width=600&height=800&seq=stain-after-pristine-clean-001&orientation=portrait" 
                      alt="After Stain Removal" 
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-4 right-4 bg-teal-500/90 px-3 py-1 rounded-full text-xs font-bold text-white">
                      AFTER
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-drop-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Stain Removal</h3>
                <p className="text-gray-300 mb-4">
                  Professional extraction and treatment to eliminate tough stains from upholstery and carpets
                </p>
                <p className="text-teal-500 font-bold text-lg mb-4">Starting at $29</p>
                <button onClick={() => scrollToSection('contact')} className="text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Plastic Restoration Before/After */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer group">
              <div className="grid grid-cols-2 h-96">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Real%20faded%20gray%20car%20plastic%20trim%20from%20sun%20exposure%20and%20weathering%2C%20authentic%20vehicle%20exterior%20trim%20showing%20realistic%20color%20loss%20and%20oxidation%2C%20genuine%20automobile%20plastic%20molding%20with%20natural%20fading%20from%20UV%20damage%2C%20actual%20car%20trim%20with%20visible%20gray%20discoloration%20from%20age%2C%20everyday%20vehicle%20exterior%20plastic%20needing%20restoration%20with%20realistic%20weathering&width=600&height=800&seq=plastic-before-realistic-001&orientation=portrait" 
                    alt="Before Plastic Restoration" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 px-3 py-1 rounded-full text-xs font-bold text-gray-900">
                    BEFORE
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Real%20car%20plastic%20trim%20after%20restoration%20showing%20darker%20color%20and%20improved%20appearance%2C%20authentic%20vehicle%20exterior%20trim%20after%20treatment%20with%20natural%20black%20finish%20restored%2C%20genuine%20automobile%20plastic%20molding%20after%20restoration%20with%20visible%20color%20improvement%2C%20actual%20car%20trim%20after%20professional%20treatment%20with%20realistic%20enhanced%20darkness%2C%20everyday%20vehicle%20exterior%20plastic%20after%20restoration%20with%20natural%20improved%20condition&width=600&height=800&seq=plastic-after-realistic-001&orientation=portrait" 
                    alt="After Plastic Restoration" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 right-4 bg-teal-500/90 px-3 py-1 rounded-full text-xs font-bold text-white">
                    AFTER
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <span className="text-white font-semibold text-lg">Plastic Restoration Transformation</span>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xl text-gray-700 mb-6">Ready for your transformation?</p>
            <button 
              onClick={() => scrollToSection('contact')}
              className="bg-teal-500 text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-teal-600 transition-all hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SCHEDULE DETAILING
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-teal-500 text-sm font-bold tracking-widest uppercase mb-4">
              PRICING
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Choose Your Service Level
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              All packages include mobile service at your location
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* BasicPackage */}
            <div className="bg-gray-800 border border-gray-700 rounded-3xl p-8 hover:shadow-2xl hover:scale-105 transition-all">
              <h3 className="text-2xl font-bold text-white mb-4">Basic Detail</h3>
              <div className="mb-6">
                <span className="text-gray-400 text-sm block mb-1">Varies on Car Size</span>
                <span className="text-gray-400 text-sm">from</span>
                <div className="text-5xl font-bold text-white">$99</div>
              </div>
              <div className="h-px bg-gray-700 mb-6"></div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Exterior hand wash</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Wheel and tire cleaning</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Interior vacuum</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Window cleaning</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Dashboard wipe down</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Tire shine application</span>
                </li>
              </ul>
              <button 
                onClick={() => scrollToSection('contact')}
                className="w-full bg-transparent border-2 border-gray-600 text-white py-4 rounded-full font-semibold hover:bg-gray-700 hover:border-gray-500 transition-all whitespace-nowrap cursor-pointer"
              >
                SELECT PACKAGE
              </button>
            </div>

            {/* PremiumPackage */}
            <div className="bg-gray-800 border-2 border-teal-500 rounded-3xl p-8 hover:shadow-2xl hover:scale-105 transition-all relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-teal-500/90 text-white px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap">
                  MOST POPULAR
                </span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 mt-4">Premium Detail</h3>
              <div className="mb-6">
                <span className="text-gray-400 text-sm block mb-1">Varies on Car Size</span>
                <span className="text-gray-400 text-sm">from</span>
                <div className="text-5xl font-bold text-white">$199</div>
              </div>
              <div className="h-px bg-gray-700 mb-6"></div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Everything in Basic</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Pre hand wash</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Drill brush scrub down</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Deep interior cleaning</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Leather conditioning</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Carpet shampoo</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Plastic and vinyl protectant</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Drying aid protectant</span>
                </li>
              </ul>
              <button 
                onClick={() => scrollToSection('contact')}
                className="w-full bg-teal-500 text-white py-4 rounded-full font-semibold hover:bg-teal-600 transition-all whitespace-nowrap cursor-pointer"
              >
                SELECT PACKAGE
              </button>
            </div>

            {/* ElitePackage */}
            <div className="bg-gray-800 border border-gray-700 rounded-3xl p-8 hover:shadow-2xl hover:scale-105 transition-all">
              <h3 className="text-2xl font-bold text-white mb-4">Elite Detail</h3>
              <div className="mb-6">
                <span className="text-gray-400 text-sm block mb-1">Varies on Car Size</span>
                <span className="text-gray-400 text-sm">from</span>
                <div className="text-5xl font-bold text-white">$399</div>
              </div>
              <div className="h-px bg-gray-700 mb-6"></div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Everything in Premium</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Ceramic coating spray application</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span> Foam wax application</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Engine bay detail</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Carpet stripes</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Headlight restoration</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Pet hair removal</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Trim restoration</span>
                </li>
              </ul>
              <button 
                onClick={() => scrollToSection('contact')}
                className="w-full bg-transparent border-2 border-gray-600 text-white py-4 rounded-full font-semibold hover:bg-gray-700 hover:border-gray-500 transition-all whitespace-nowrap cursor-pointer"
              >
                SELECT PACKAGE
              </button>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-400 text-lg mb-2">You can always add on a service to your package if it's not in it already</p>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-teal-500 font-semibold hover:text-teal-400 transition-colors flex items-center gap-2 mx-auto whitespace-nowrap cursor-pointer"
            >
              <span>Add a Service to Your Package</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-teal-500 text-sm font-bold tracking-widest uppercase mb-4">
              TESTIMONIALS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Casual%20friendly%20guy%20smiling%20naturally%20in%20everyday%20clothes%2C%20relaxed%20male%20customer%20with%20genuine%20smile%20wearing%20casual%20shirt%2C%20approachable%20man%20with%20warm%20friendly%20expression%20in%20informal%20setting%2C%20real%20person%20casual%20portrait%20with%20authentic%20happy%20smile%2C%20everyday%20guy%20with%20natural%20friendly%20demeanor&width=200&height=200&seq=casual-customer-portrait-004&orientation=squarish" 
                  alt="Customer" 
                  className="w-16 h-16 rounded-full object-cover object-top"
                />
                <div>
                  <div className="font-bold text-gray-900">Michael Rodriguez</div>
                  <div className="text-sm text-gray-600">Verified Customer</div>
                </div>
              </div>
              <div className="text-6xl text-teal-500 mb-4">"</div>
              <p className="text-gray-700 mb-6">
                Absolutely incredible service! They came to my office and detailed my car while I worked. The results were amazing - my 5-year-old car looks brand new. Highly recommend!
              </p>
              <div className="flex gap-1">
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20satisfied%20female%20customer%20smiling%20genuinely%2C%20authentic%20happy%20client%20testimonial%20photo%2C%20real%20business%20professional%20woman%20portrait%20with%20warm%20expression%2C%20genuine%20person%20headshot%20for%20customer%20review%2C%20friendly%20confident%20woman%20portrait&width=200&height=200&seq=customer-portrait-002&orientation=squarish" 
                  alt="Customer" 
                  className="w-16 h-16 rounded-full object-cover object-top"
                />
                <div>
                  <div className="font-bold text-gray-900">Sarah Thompson</div>
                  <div className="text-sm text-gray-600">Verified Customer</div>
                </div>
              </div>
              <div className="text-6xl text-teal-500 mb-4">"</div>
              <p className="text-gray-700 mb-6">
                The ceramic coating package was worth every penny. My car stays cleaner longer and the shine is unbelievable. The team was professional and thorough. Best detailing experience ever!
              </p>
              <div className="flex gap-1">
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20satisfied%20male%20customer%20with%20genuine%20smile%2C%20authentic%20happy%20client%20testimonial%20photo%2C%20real%20business%20professional%20man%20portrait%20with%20positive%20expression%2C%20genuine%20person%20headshot%20for%20customer%20review%2C%20approachable%20professional%20individual%20portrait&width=200&height=200&seq=customer-portrait-003&orientation=squarish" 
                  alt="Customer" 
                  className="w-16 h-16 rounded-full object-cover object-top"
                />
                <div>
                  <div className="font-bold text-gray-900">David Chen</div>
                  <div className="text-sm text-gray-600">Verified Customer</div>
                </div>
              </div>
              <div className="text-6xl text-teal-500 mb-4">"</div>
              <p className="text-gray-700 mb-6">
                I have two dogs and my car interior was a disaster. They completely transformed it - removed all the pet hair and odors. The convenience of mobile service is unbeatable!
              </p>
              <div className="flex gap-1">
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact/CTA Section */}
      <section id="contact" className="relative min-h-screen flex items-center justify-center py-24">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/4855067b8186d18c626a2155d1a193ae/1deb6f66fef4dbfbd83a687049d0387c.jpeg" 
            alt="Contact Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-wide uppercase">
            READY TO EXPERIENCE PREMIUM MOBILE DETAILING?
          </h2>
          <p className="text-xl md:text-2xl text-white mb-12">
            We come to your location • Flexible scheduling • Professional results
          </p>

          <button 
            onClick={() => {
              const contactForm = document.getElementById('contact-form-section');
              if (contactForm) {
                contactForm.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-gray-900 text-white px-12 py-6 rounded-full font-bold text-lg hover:bg-gray-800 transition-all hover:shadow-2xl hover:scale-105 flex items-center gap-4 mx-auto whitespace-nowrap cursor-pointer"
          >
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
              <i className="ri-calendar-line text-white text-xl"></i>
            </div>
            <span>BOOK YOUR DETAIL</span>
            <i className="ri-arrow-right-up-line text-2xl"></i>
          </button>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form-section" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get Your Free Quote</h2>
            <p className="text-lg text-gray-600">
              Fill out the form below and we'll get back to you within 24 hours
            </p>
          </div>

          <form 
            action="https://readdy.ai/api/form/d5demcmfmrgp9cvlvi9g"
            method="POST"
            className="bg-gray-50 rounded-2xl p-8 shadow-lg" 
            data-readdy-form 
            id="booking-form"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              // Validate required fields
              const requiredFields = ['first_name', 'last_name', 'email', 'phone', 'service_package', 'vehicle_info', 'location'];
              for (const field of requiredFields) {
                if (!formData.get(field)) {
                  alert('Please fill in all required fields');
                  return;
                }
              }

              // Validate message length
              const message = formData.get('message') as string;
              if (message && message.length > 500) {
                alert('Message must be 500 characters or less');
                return;
              }

              // Collect checkbox values
              const additionalServices = [];
              if (formData.get('pet_hair_removal')) additionalServices.push('Pet Hair Removal');
              if (formData.get('headlight_restoration')) additionalServices.push('Headlight Restoration');
              if (formData.get('engine_bay_cleaning')) additionalServices.push('Engine Bay Cleaning');
              
              // Create URL encoded data
              const submitData = new URLSearchParams();
              submitData.append('first_name', formData.get('first_name') as string);
              submitData.append('last_name', formData.get('last_name') as string);
              submitData.append('email', formData.get('email') as string);
              submitData.append('phone', formData.get('phone') as string);
              submitData.append('service_package', formData.get('service_package') as string);
              submitData.append('vehicle_info', formData.get('vehicle_info') as string);
              submitData.append('location', formData.get('location') as string);
              if (additionalServices.length > 0) {
                submitData.append('additional_services', additionalServices.join(', '));
              }
              if (message) {
                submitData.append('message', message);
              }

              // Submit form
              fetch('https://readdy.ai/api/form/d5demcmfmrgp9cvlvi9g', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: submitData.toString()
              })
              .then(response => {
                if (response.ok) {
                  alert('Thank you! Your quote request has been submitted successfully. We will contact you within 24 hours.');
                  form.reset();
                } else {
                  alert('There was an error submitting your request. Please try again.');
                }
              })
              .catch(() => {
                alert('There was an error submitting your request. Please try again.');
              });
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  First Name *
                </label>
                <input 
                  type="text" 
                  name="first_name"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Last Name *
                </label>
                <input 
                  type="text" 
                  name="last_name"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Email *
                </label>
                <input 
                  type="email" 
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone *
                </label>
                <input 
                  type="tel" 
                  name="phone"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Service Package *
              </label>
              <select 
                name="service_package"
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm cursor-pointer"
              >
                <option value="">Select a package</option>
                <option value="Detail Service">Detail Service</option>
                <option value="Consultation">Consultation</option>
                <option value="Basic Detail - $99">Basic Detail - $99</option>
                <option value="Premium Detail - $199">Premium Detail - $199</option>
                <option value="Elite Detail - $399">Elite Detail - $399</option>
                <option value="Custom Quote">Custom Quote</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Vehicle Information *
              </label>
              <input 
                type="text" 
                name="vehicle_info"
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                placeholder="Year, Make, Model (e.g., 2020 Honda Accord)"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Preferred Location *
              </label>
              <input 
                type="text" 
                name="location"
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                placeholder="Home, Office, or Other Address"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Additional Services (Optional)
              </label>
              <select 
                name="additional_services"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm cursor-pointer"
              >
                <option value="">Select a service</option>
                <option value="Pet Hair Removal - Starting at $29">Pet Hair Removal - Starting at $29</option>
                <option value="Headlight Restoration - Starting at $49">Headlight Restoration - Starting at $49</option>
                <option value="Engine Bay Cleaning - Starting at $49">Engine Bay Cleaning - Starting at $49</option>
                <option value="Scratch Removal - Starting at $29">Scratch Removal - Starting at $29</option>
                <option value="Clay Bar Treatment - Starting at $99">Clay Bar Treatment - Starting at $99</option>
                <option value="Stain Removal - Starting at $29">Stain Removal - Starting at $29</option>
                <option value="Plastic Restoration - Starting at $39">Plastic Restoration - Starting at $39</option>
                <option value="Ceramic Coating - Starting at $99">Ceramic Coating - Starting at $99</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Message (Optional)
              </label>
              <textarea 
                name="message"
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm resize-none"
                placeholder="Any special requests or questions? (Max 500 characters)"
              ></textarea>
            </div>

            <button 
              type="submit"
              className="w-full bg-teal-500 text-white py-4 rounded-full font-bold text-lg hover:bg-teal-600 transition-all hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              REQUEST QUOTE
            </button>
          </form>
        </div>
      </section>

      {/* Consultation Section */}
      <section id="consultation" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Schedule Your Free Consultation</h2>
            <p className="text-xl mb-8 text-gray-600">
              Not sure what your vehicle needs? Book a free consultation with our detailing experts. We'll assess your vehicle's condition and recommend the perfect services tailored to your needs and budget.
            </p>
            <div className="bg-white rounded-2xl shadow-xl p-8 text-left">
              <div className="mb-8 grid md:grid-cols-3 gap-6 text-center">
                <div className="p-4">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-calendar-check-line text-3xl text-teal-600"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">No Obligation</h3>
                  <p className="text-sm text-gray-600">Free assessment with zero pressure to commit</p>
                </div>
                <div className="p-4">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-user-star-line text-3xl text-teal-600"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Expert Advice</h3>
                  <p className="text-sm text-gray-600">Get professional recommendations from certified detailers</p>
                </div>
                <div className="p-4">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-price-tag-3-line text-3xl text-teal-600"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Custom Quote</h3>
                  <p className="text-sm text-gray-600">Receive a personalized quote based on your vehicle's needs</p>
                </div>
              </div>

              <form 
                id="consultation-form"
                data-readdy-form
                onSubmit={handleConsultationSubmit}
              >
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Preferred Contact Method *</label>
                    <select
                      name="contact_method"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Select method</option>
                      <option value="Phone">Phone Call</option>
                      <option value="Email">Email</option>
                      <option value="Text">Text Message</option>
                    </select>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">Vehicle Information *</label>
                  <input
                    type="text"
                    name="vehicle_info"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Year, Make, Model (e.g., 2020 Toyota Camry)"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">What concerns do you have about your vehicle? *</label>
                  <div className="space-y-2 mb-4">
                    <label className="flex items-center">
                      <input type="checkbox" name="concerns" value="Paint condition/scratches" className="mr-2" />
                      <span className="text-gray-700">Paint condition/scratches</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" name="concerns" value="Interior stains/odors" className="mr-2" />
                      <span className="text-gray-700">Interior stains/odors</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" name="concerns" value="Pet hair" className="mr-2" />
                      <span className="text-gray-700">Pet hair</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" name="concerns" value="Headlight clarity" className="mr-2" />
                      <span className="text-gray-700">Headlight clarity</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" name="concerns" value="Long-term protection" className="mr-2" />
                      <span className="text-gray-700">Long-term protection needed</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" name="concerns" value="Not sure" className="mr-2" />
                      <span className="text-gray-700">Not sure - need expert assessment</span>
                    </label>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">Preferred Date & Time</label>
                  <input
                    type="text"
                    name="preferred_datetime"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="e.g., Next Tuesday afternoon, or any weekday morning"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">Additional Information</label>
                  <textarea
                    name="additional_info"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Tell us more about what you're looking for or any questions you have..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-teal-600 text-white font-bold py-4 px-8 rounded-lg hover:bg-teal-700 transition duration-300 whitespace-nowrap"
                >
                  Schedule Free Consultation
                </button>
                {consultationSubmitStatus && (
                  <div className={`mt-4 p-4 rounded-lg ${consultationSubmitStatus === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {consultationSubmitStatus === 'success' ? 'Thank you! We\'ll contact you soon to schedule your consultation.' : 'Something went wrong. Please try again.'}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/4855067b8186d18c626a2155d1a193ae/154d0532d8fc27f45dd426f8e055f5b4.png" 
                  alt="Logo" 
                  className="h-10 w-10 object-contain"
                />
                <span className="text-xl font-bold">Reflecto Auto Detailing</span>
              </div>
              <p className="text-gray-400 text-sm">
                Premium mobile car detailing services at your doorstep
              </p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">SERVICES</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#services" className="hover:text-teal-500 transition-colors cursor-pointer">Exterior Detail</a></li>
                <li><a href="#services" className="hover:text-teal-500 transition-colors cursor-pointer">Interior Detail</a></li>
                <li><a href="#services" className="hover:text-teal-500 transition-colors cursor-pointer">Paint Correction</a></li>
                <li><a href="#services" className="hover:text-teal-500 transition-colors cursor-pointer">Ceramic Coating</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">COMPANY</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('about')} className="hover:text-teal-500 transition-colors cursor-pointer">About Us</button></li>
                <li><button onClick={() => scrollToSection('gallery')} className="hover:text-teal-500 transition-colors cursor-pointer">Gallery</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-teal-500 transition-colors cursor-pointer">Pricing</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-teal-500 transition-colors cursor-pointer">Contact</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">CONNECT</h4>
              <div className="flex gap-4 mb-6">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-500 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-500 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-500 transition-colors cursor-pointer">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
              </div>
              <p className="text-gray-400 text-sm mb-2">Get detailing tips and exclusive offers</p>
              <div className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 rounded-full bg-gray-800 border border-gray-700 focus:border-teal-500 outline-none text-sm"
                />
                <button className="bg-teal-500 px-6 py-2 rounded-full font-semibold hover:bg-teal-600 transition-colors text-sm whitespace-nowrap cursor-pointer">
                  SUBSCRIBE
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © 2025 Reflecto Auto Detailing. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 text-sm hover:text-teal-500 transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
